// v51 — Topbar on the dark control-room shell.
//
// Wears `lc-v2` for the same reason the Sidebar does: the v2 tokens are on a
// class, not :root, so restyling the header does not retint 117 routes in the
// same commit. See the block in index.css.
//
// Sticky, and it stays sticky — the header and the rail are the two surfaces
// you navigate with, so neither scrolls away from the content.
//
// v49.2 / v49.12 — Topbar.
// Sticky bar above the main content. Page title + active tenant pill +
// notifications bell (v49.12 — unread count + dropdown preview).

import { useLocation } from "react-router";
import { useShellUser } from "@/features/auth/AuthContext";
import { pageTitleFor } from "../nav";
import { NotificationsBell } from "./NotificationsBell";
import { CompanySwitcher } from "./CompanySwitcher";

interface TopbarProps {
  onOpenSearch?: () => void;
}

export function Topbar({ onOpenSearch }: TopbarProps = {}) {
  const { pathname } = useLocation();
  const user  = useShellUser();
  const title = pageTitleFor(pathname, user?.role);

  return (
    <header
      role="banner"
      className="lc-v2 sticky top-0 z-10 backdrop-blur-md px-6 py-3 flex items-center gap-4"
      /* THE BAR IS LIGHT NOW — matched to the client's prototype, where
         `.topbar` is `color-mix(in srgb, var(--surface) 92%, transparent)`
         over a white surface.

         Rather than recolour this file AND CompanySwitcher AND
         NotificationsBell AND the search button, the shell tokens are REBOUND
         here. Every child already reads --lc-shell-*, so pointing those at the
         light palette flips the whole bar in one place — and a component added
         tomorrow inherits the right colours for free.

         The dark values still live in :root for the sidebar, which stays dark. */
      style={{
        "--lc-shell":       "var(--color-lc-surface, #fff)",
        "--lc-shell-2":     "var(--color-lc-bg-2)",
        "--lc-shell-line":  "var(--color-lc-line)",
        "--lc-shell-fg":    "var(--color-lc-ink)",
        "--lc-shell-mute":  "var(--color-lc-mute)",
        "--lc-shell-faint": "var(--color-lc-faint)",
        "--lc-shell-accent":"var(--color-lc-blue-dark)",
        background: "color-mix(in srgb, var(--lc-shell) 92%, transparent)",
        borderBottom: "1px solid var(--lc-shell-line)",
      } as React.CSSProperties}
    >
      {/* Weight discipline: headings cap at 700. font-extrabold was 800. */}
      <h1
        className="text-[15px] font-bold tracking-tight flex-1 min-w-0 truncate"
        style={{ color: "var(--lc-shell-fg)" }}
      >
        {title}
      </h1>

      {/* Global search trigger — Cmd-K hint on the right. */}
      {onOpenSearch && (
        <button
          type="button"
          onClick={onOpenSearch}
          className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-lc-md text-[12px] font-semibold transition-colors"
          style={{
            background: "var(--lc-shell-2)",
            border: "1px solid var(--lc-shell-line)",
            color: "var(--lc-shell-mute)",
          }}
          aria-label="Search the platform"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span>Search</span>
          <kbd
            className="px-1.5 py-0.5 rounded-lc-sm text-[10px] font-semibold leading-none"
            style={{ border: "1px solid var(--lc-shell-line)", color: "var(--lc-shell-faint)" }}
          >⌘K</kbd>
        </button>
      )}

      {/* Acting company. Renders the same static pill this used to be for the
          30-of-31 users who belong to one company, and a dropdown for anyone
          who belongs to more than one. Falls back to the profile's company
          string until the memberships have hydrated, so the bar never flickers
          empty on first paint. */}
      {user && <CompanySwitcher fallbackName={user.company} />}

      {user && <NotificationsBell />}
    </header>
  );
}
